import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ChevronDoubleDownIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  PlayIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import VideoHeaderStrip, { formatDuration } from '../components/VideoHeaderStrip';
import { findingsAPI } from '../services/findingsApi';
import { assessmentAPI } from '../services/assessmentApi';
import { getApiErrorMessage } from '../utils/apiError';
import { formatFindingRange, formatFindingTime } from '../utils/findingTime';

/**
 * The Review Queue tab (RUNLOG V8; the 2026-09-25 brief, section 6): the rows of GRIHA's own
 * site-visit report, as the assessor wrote them from the whole run, for the auditor to check,
 * correct and decide before the Report tab prints them.
 *
 * Left: the video context for the row in focus - its photo, what the camera saw in that shot,
 * what was said, and a View Criteria link into the standard at that appraisal. Right: one
 * block per report row, mirroring GRIHA's four columns, every one editable:
 *   Criteria           a dropdown of the report's rows (appraisal text only), to correct a
 *                      row the model answered in the wrong place
 *   Compliance on Site Yes / No / Partly / Not Applicable / To Be Verified
 *   Observation        the model's text, plus the "Action to be taken" GRIHA puts after it
 *   Reference Image    frames the model offered; the auditor chooses which go in the report
 *                      and can rewrite each caption
 * Approve confirms a row; Reject leaves it out of the report; Reset undoes either.
 *
 * "Write a new version" never takes the rows away mid-review: the server keeps returning the
 * version on screen as `assessment` and the one being written as `attempt`, and the page
 * switches only once the new version is ready (or says it failed, and keeps these rows).
 */

const STATUS_PILLS = {
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
};

// White text on each at 4.5:1 or better (WCAG AA, QA-46): green-600 (3.30:1) and amber-500
// (2.15:1) failed, so Yes and Partly use the 700 shades (5.02:1 each).
const COMPLIANCE_TONES = {
  yes: 'border-green-700 bg-green-700 text-white',
  no: 'border-red-600 bg-red-600 text-white',
  partly: 'border-amber-700 bg-amber-700 text-white',
  not_applicable: 'border-gray-500 bg-gray-500 text-white',
  to_be_verified: 'border-blue-600 bg-blue-600 text-white',
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All rows' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

function toDraft(entry) {
  return {
    row_id: entry.row_id,
    compliance: entry.compliance,
    observation: entry.observation || '',
    action: entry.action || '',
    selected: (entry.selected || []).map((s) => ({ frame: s.frame, caption: s.caption || '' })),
  };
}

/** Only the columns that differ from what the server holds: the PATCH body. */
export function changedFields(draft, entry) {
  const base = toDraft(entry);
  const patch = {};
  ['row_id', 'compliance', 'observation', 'action'].forEach((key) => {
    if (draft[key] !== base[key]) patch[key] = draft[key];
  });
  if (JSON.stringify(draft.selected) !== JSON.stringify(base.selected)) {
    patch.selected = draft.selected;
  }
  return patch;
}

function withEntry(data, updated) {
  if (!data) return data;
  const entries = data.entries.map((e) => (e.id === updated.id ? updated : e));
  const count = (status) => entries.filter((e) => e.status === status).length;
  return {
    ...data,
    entries,
    counts: {
      total: entries.length,
      pending: count('pending'),
      approved: count('approved'),
      rejected: count('rejected'),
    },
  };
}

function truncate(text, length) {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
}

function RowText({ row }) {
  if (!row) return null;
  return (
    <div className="mt-2 rounded-md bg-[#F8F8FF] border border-gray-200 px-3 py-2 text-sm text-gray-800">
      <p className="text-xs italic text-gray-500 mb-1">
        Appraisal {row.appraisal}: {row.appraisal_text}
      </p>
      <p>{row.text}</p>
      {row.items?.length > 0 && (
        <ul className="mt-1 list-disc list-inside text-gray-700">
          {row.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className="italic">
              {item.connector ? `${item.connector} ` : ''}
              {item.label ? `${item.label}: ` : ''}
              {item.text}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-1 flex flex-wrap gap-2 text-xs">
        {row.mandatory && <span className="font-semibold text-red-700">(Mandatory)</span>}
        {row.needs_documents && (
          <span className="text-blue-800">Only documents can prove this row: at best To Be Verified from a video</span>
        )}
      </div>
    </div>
  );
}

function FocusPanel({ focus, video, standardId }) {
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const { entry, candidate, row } = focus || {};

  useEffect(() => {
    setShowVideo(false);
  }, [entry?.id, candidate?.frame]);

  const playFrom = () => {
    setShowVideo(true);
    // The element mounts with this render; seek once it has metadata (onLoadedMetadata),
    // or straight away when it is already loaded.
    const player = videoRef.current;
    if (player && candidate && player.readyState >= 1) {
      player.currentTime = candidate.start_s || 0;
    }
  };

  if (!entry) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-500">
        Choose a row on the right to see its evidence here.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="focus-panel">
      <div className="bg-black">
        {candidate ? (
          <img
            src={candidate.url}
            alt={candidate.caption || `Frame at ${formatFindingTime(candidate.start_s, video?.kind)}`}
            className="w-full max-h-[340px] object-contain"
          />
        ) : (
          <div className="h-48 flex flex-col items-center justify-center text-sm text-gray-400 px-6 text-center">
            <PhotoIcon className="h-8 w-8 mb-2" />
            No frame was offered for this row: nothing relevant was seen on camera.
          </div>
        )}
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {row ? `${row.appraisal} · ${truncate(row.text, 60)}` : entry.row_id}
          </p>
          {candidate && video?.file_url && (
            <button
              type="button"
              onClick={playFrom}
              className="inline-flex items-center text-xs font-medium text-blue-700 hover:text-blue-900"
            >
              <PlayIcon className="h-4 w-4 mr-1" />
              Play from {formatFindingTime(candidate.start_s, video?.kind)}
            </button>
          )}
        </div>
        {showVideo && video?.file_url && (
          <video
            ref={videoRef}
            src={video.file_url}
            controls
            autoPlay
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = candidate?.start_s || 0;
            }}
            className="w-full max-h-[260px] bg-black rounded"
          />
        )}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            What the camera saw
          </h4>
          <p className="text-sm text-gray-800">
            {candidate?.description || (candidate ? 'No description was recorded for this shot.' : '—')}
          </p>
          {candidate && (
            <p className="mt-1 text-xs text-gray-500">
              {video?.kind === 'photos'
                ? `${formatFindingTime(candidate.start_s, video?.kind)} in the set`
                : `${formatFindingRange(candidate.start_s, candidate.end_s, video?.kind)} in the recording`}
            </p>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">What was said</h4>
          {entry.said?.length ? (
            <ul className="space-y-1.5" data-testid="said-lines">
              {entry.said.map((line) => (
                <li key={line.id} className="text-sm text-gray-800">
                  <span className="text-xs text-gray-500 mr-1">
                    {formatDuration(line.start_s)} · {line.speaker}
                  </span>
                  {line.text}
                  {line.english && <span className="block text-xs italic text-gray-600">{line.english}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No speech was cited for this row.</p>
          )}
        </div>
        {standardId && (
          <Link
            to={`/standards/${standardId}${row?.requirement_id ? `?requirement=${row.requirement_id}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF]"
          >
            <BookOpenIcon className="h-4 w-4 mr-1.5" />
            View criteria
          </Link>
        )}
      </div>
    </div>
  );
}

function EntryBlock({
  entry,
  catalogueGroups,
  catalogueById,
  options,
  focusedFrame,
  isFocused,
  onFocus,
  onSave,
  onReview,
  onDraftRow,
  maxPhotos,
  busy,
  videoKind,
}) {
  const [draft, setDraft] = useState(() => toDraft(entry));
  // A new copy of the entry from the server replaces the draft when its content changed (a
  // save, which may have normalised what was typed) or when nothing is being edited. A
  // status-only change (Approve, Reject, Reset) keeps unsaved edits instead of wiping them.
  const shownEntry = useRef(entry);
  useEffect(() => {
    const before = shownEntry.current;
    shownEntry.current = entry;
    if (before === entry) return;
    const contentChanged = Object.keys(changedFields(toDraft(entry), before)).length > 0;
    setDraft((prev) =>
      contentChanged || Object.keys(changedFields(prev, before)).length === 0 ? toDraft(entry) : prev
    );
  }, [entry]);
  // The left panel describes the row chosen in the Criteria dropdown, saved or not.
  useEffect(() => {
    onDraftRow(entry.id, draft.row_id);
  }, [entry.id, draft.row_id, onDraftRow]);

  const patch = changedFields(draft, entry);
  const dirty = Object.keys(patch).length > 0;
  const row = catalogueById[draft.row_id];
  const savedRow = catalogueById[entry.row_id];
  const status = STATUS_PILLS[entry.status] || STATUS_PILLS.pending;
  const selectedIndex = (frame) => draft.selected.findIndex((s) => s.frame === frame);

  const atPhotoLimit = draft.selected.length >= maxPhotos;
  const toggleSelected = (candidate) => {
    setDraft((prev) => {
      const at = prev.selected.findIndex((s) => s.frame === candidate.frame);
      if (at < 0 && prev.selected.length >= maxPhotos) return prev;
      const selected =
        at >= 0
          ? prev.selected.filter((s) => s.frame !== candidate.frame)
          : [...prev.selected, { frame: candidate.frame, caption: candidate.caption || '' }];
      return { ...prev, selected };
    });
  };

  const save = () => onSave(entry.id, patch);
  const decide = async (action) => {
    if (dirty && action !== 'reset') {
      const saved = await save();
      if (!saved) return;
    }
    onReview(entry.id, action);
  };

  return (
    <div
      id={`entry-${entry.id}`}
      data-testid="entry-block"
      onFocusCapture={() =>
        !isFocused && onFocus(entry.id, draft.selected[0]?.frame || entry.candidates[0]?.frame)
      }
      className={`bg-white rounded-lg border shadow-sm ${
        isFocused ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'
      } ${entry.status === 'rejected' ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
            {status.label}
          </span>
          {entry.edited_fields?.length > 0 && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
              Edited
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onFocus(entry.id, draft.selected[0]?.frame || entry.candidates[0]?.frame)}
          className="text-xs font-medium text-blue-700 hover:text-blue-900"
        >
          Show evidence
        </button>
      </div>

      <div className="p-4 space-y-5">
        <div>
          <label htmlFor={`criteria-${entry.id}`} className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Criteria
            {/* QA 2026-09-29: every row's dropdown was announced as the same "Criteria".
                The saved row names it, so a screen reader can tell the fourteen apart. */}
            {savedRow && <span className="sr-only">{` for ${savedRow.appraisal}: ${truncate(savedRow.text, 60)}`}</span>}
          </label>
          <select
            id={`criteria-${entry.id}`}
            value={draft.row_id}
            onChange={(e) => setDraft((prev) => ({ ...prev, row_id: e.target.value }))}
            className="mt-1 w-full py-2 pl-3 pr-8 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {catalogueGroups.map((group) => (
              <optgroup key={group.criterion.criterion_id} label={`Criterion ${group.criterion.number}: ${group.criterion.title}`}>
                {group.rows.map((option) => (
                  <option key={option.row_id} value={option.row_id}>
                    {option.appraisal} · {truncate(option.text, 80)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <RowText row={row} />
        </div>

        <div>
          <span id={`compliance-${entry.id}`} className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Compliance on Site
          </span>
          <div role="radiogroup" aria-labelledby={`compliance-${entry.id}`} className="mt-1 flex flex-wrap gap-1.5">
            {options.map((option) => {
              const checked = draft.compliance === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  onClick={() => setDraft((prev) => ({ ...prev, compliance: option.value }))}
                  className={`px-3 py-1.5 rounded-md border text-sm font-medium ${
                    checked
                      ? COMPLIANCE_TONES[option.value]
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-[#F8F8FF]'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor={`observation-${entry.id}`} className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Observation
          </label>
          <textarea
            id={`observation-${entry.id}`}
            rows={5}
            value={draft.observation}
            onChange={(e) => setDraft((prev) => ({ ...prev, observation: e.target.value }))}
            className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <label htmlFor={`action-${entry.id}`} className="mt-2 block text-xs font-medium text-gray-600">
            Action to be taken
          </label>
          <textarea
            id={`action-${entry.id}`}
            rows={2}
            value={draft.action}
            placeholder="Nothing needed"
            onChange={(e) => setDraft((prev) => ({ ...prev, action: e.target.value }))}
            className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Reference Image</span>
          {entry.candidates.length === 0 ? (
            <p className="mt-1 text-sm text-gray-500">No frame was offered for this row.</p>
          ) : (
            <div className="mt-1 grid grid-cols-3 gap-2">
              {entry.candidates.map((candidate) => {
                const at = selectedIndex(candidate.frame);
                const focused = isFocused && focusedFrame === candidate.frame;
                return (
                  <div
                    key={candidate.frame}
                    className={`rounded-md border overflow-hidden ${
                      at >= 0 ? 'border-blue-500' : 'border-gray-200'
                    } ${focused ? 'ring-2 ring-blue-300' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => onFocus(entry.id, candidate.frame)}
                      className="relative block w-full"
                      aria-label={`Show the frame at ${formatFindingTime(candidate.start_s, videoKind)}`}
                    >
                      <img src={candidate.url} alt="" className="w-full h-20 object-cover" loading="lazy" />
                      <span className="absolute bottom-1 left-1 px-1 rounded bg-black/60 text-white text-[10px]">
                        {formatFindingTime(candidate.start_s, videoKind)}
                      </span>
                    </button>
                    <label className="flex items-center gap-1.5 px-1.5 py-1 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={at >= 0}
                        disabled={at < 0 && atPhotoLimit}
                        onChange={() => toggleSelected(candidate)}
                        aria-label={`Use the frame at ${formatFindingTime(candidate.start_s, videoKind)} in the report`}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                      />
                      {at >= 0 ? `In report (${at + 1})` : 'Use in report'}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
          {atPhotoLimit && entry.candidates.length > maxPhotos && (
            <p className="mt-1 text-xs text-gray-500" data-testid="photo-limit">
              At most {maxPhotos} photos fit one report row; untick one to choose another.
            </p>
          )}
          {draft.selected.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {draft.selected.map((photo, i) => (
                <div key={photo.frame} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-14 flex-shrink-0">Photo {i + 1}</span>
                  <input
                    type="text"
                    value={photo.caption}
                    aria-label={`Caption for photo ${i + 1}`}
                    placeholder="Caption for the report"
                    onChange={(e) => {
                      const caption = e.target.value;
                      setDraft((prev) => ({
                        ...prev,
                        selected: prev.selected.map((s) => (s.frame === photo.frame ? { ...s, caption } : s)),
                      }));
                    }}
                    className="flex-1 border border-gray-300 rounded-md text-sm px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {entry.edited_fields?.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-xs font-medium text-gray-600">What the model wrote</summary>
            <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 space-y-1 text-gray-700">
              <p>
                <span className="font-medium">Row:</span> {entry.model.row_id} ·{' '}
                <span className="font-medium">Compliance:</span>{' '}
                {options.find((o) => o.value === entry.model.compliance)?.label || entry.model.compliance}
              </p>
              <p className="whitespace-pre-line">{entry.model.observation}</p>
              {entry.model.action && <p className="whitespace-pre-line text-gray-600">{entry.model.action}</p>}
            </div>
          </details>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50/60 flex-wrap">
        <div className="flex items-center gap-2">
          {dirty && (
            <>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                Save changes
              </button>
              <button
                type="button"
                onClick={() => setDraft(toDraft(entry))}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF] disabled:opacity-50"
              >
                Discard
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {entry.status !== 'pending' && (
            <button
              type="button"
              onClick={() => decide('reset')}
              disabled={busy}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF] disabled:opacity-50"
            >
              Reset
            </button>
          )}
          {entry.status !== 'rejected' && (
            <button
              type="button"
              onClick={() => decide('reject')}
              disabled={busy}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
            >
              <XCircleIcon className="h-4 w-4 mr-1" />
              Reject
            </button>
          )}
          {entry.status !== 'approved' && (
            <button
              type="button"
              onClick={() => decide('approve')}
              disabled={busy}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-green-700 hover:bg-green-800 disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              {dirty ? 'Save and approve' : 'Approve'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReviewQueuePage() {
  const { projectId, videoId } = useParams();
  const queryClient = useQueryClient();
  const [criterionFilter, setCriterionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [focus, setFocus] = useState({ entryId: null, frame: null });
  // {entry id: row id chosen in its Criteria dropdown}, saved or not.
  const [draftRows, setDraftRows] = useState({});
  const onDraftRow = useCallback(
    (entryId, rowId) => setDraftRows((prev) => (prev[entryId] === rowId ? prev : { ...prev, [entryId]: rowId })),
    []
  );

  const videoQuery = useQuery(['video', videoId], () => findingsAPI.getVideo(videoId), {
    enabled: !!videoId,
    select: (response) => response.data,
    retry: 1,
  });

  const queryKey = ['video-assessment', videoId];
  const assessmentQuery = useQuery(queryKey, () => assessmentAPI.get(videoId), {
    enabled: !!videoId,
    select: (response) => response.data,
    retry: false,
    // While the report, or a new version of it, is being written, check back until it is done.
    refetchInterval: (data) =>
      data?.assessment?.state === 'generating' || data?.attempt?.state === 'generating' ? 5000 : false,
  });
  const data = assessmentQuery.data;

  // A new version replaces a ready one on screen once it is ready itself: say so, since the
  // rows change. Only ready-to-ready: a first version going from its spinner to its rows
  // needs no announcement.
  const shownReadyId = data?.assessment?.state === 'ready' ? data.assessment.id : null;
  const lastReadyId = useRef(shownReadyId);
  useEffect(() => {
    if (shownReadyId && lastReadyId.current && lastReadyId.current !== shownReadyId) {
      toast.success('The new version of the report is ready');
    }
    if (shownReadyId) lastReadyId.current = shownReadyId;
  }, [shownReadyId]);

  const setData = (updater) =>
    queryClient.setQueryData(queryKey, (previous) =>
      previous ? { ...previous, data: updater(previous.data) } : previous
    );

  const generateMutation = useMutation((force) => assessmentAPI.generate(videoId, { force }), {
    onSuccess: (response) => {
      setData(() => response.data);
      toast.success('Writing the GRIHA report; this takes a few minutes');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not start writing the report')),
  });

  const saveMutation = useMutation(({ entryId, patch }) => assessmentAPI.updateEntry(videoId, entryId, patch), {
    onSuccess: (response, { patch }) => {
      setData((current) => withEntry(current, response.data));
      // A Criteria move can leave the old row unanswered, and the server then adds a pending
      // row for it: read the list again so it appears.
      if (patch.row_id) queryClient.invalidateQueries(queryKey);
      toast.success('Row saved');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not save this row')),
  });

  const reviewMutation = useMutation(
    ({ entryId, action }) => assessmentAPI.reviewEntry(videoId, entryId, action),
    {
      onSuccess: (response) => setData((current) => withEntry(current, response.data)),
      onError: (error) => toast.error(getApiErrorMessage(error, 'Could not update this row')),
    }
  );

  const onSave = async (entryId, patch) => {
    try {
      await saveMutation.mutateAsync({ entryId, patch });
      return true;
    } catch (error) {
      return false;
    }
  };
  const onReview = (entryId, action) => reviewMutation.mutate({ entryId, action });

  const catalogue = useMemo(() => data?.catalogue || [], [data]);
  const catalogueById = useMemo(() => Object.fromEntries(catalogue.map((row) => [row.row_id, row])), [catalogue]);
  const criteria = useMemo(() => data?.criteria || [], [data]);
  const catalogueGroups = useMemo(
    () =>
      criteria.map((criterion) => ({
        criterion,
        rows: catalogue.filter((row) => row.criterion_id === criterion.criterion_id),
      })),
    [criteria, catalogue]
  );
  const entries = useMemo(() => data?.entries || [], [data]);
  const rowIndex = useMemo(() => Object.fromEntries(catalogue.map((row, i) => [row.row_id, i])), [catalogue]);

  const visible = useMemo(() => {
    const byOrder = entries
      .slice()
      .sort((a, b) => (rowIndex[a.row_id] ?? 999) - (rowIndex[b.row_id] ?? 999));
    return byOrder.filter((entry) => {
      const row = catalogueById[entry.row_id];
      if (criterionFilter !== 'all' && row?.criterion_id !== criterionFilter) return false;
      if (statusFilter !== 'all' && entry.status !== statusFilter) return false;
      return true;
    });
  }, [entries, rowIndex, catalogueById, criterionFilter, statusFilter]);

  const pending = useMemo(() => visible.filter((e) => e.status === 'pending'), [visible]);

  // Focus the first row once the rows arrive, and again when a new version replaces the rows,
  // so the left panel is never empty.
  useEffect(() => {
    const stale = focus.entryId && !entries.some((e) => e.id === focus.entryId);
    if ((!focus.entryId || stale) && visible.length > 0) {
      const first = pending[0] || visible[0];
      setFocus({ entryId: first.id, frame: first.selected[0]?.frame || first.candidates[0]?.frame || null });
    }
  }, [visible, pending, entries, focus.entryId]);

  const focusedEntry = entries.find((e) => e.id === focus.entryId);
  const focusedCandidate =
    focusedEntry?.candidates.find((c) => c.frame === focus.frame) ||
    focusedEntry?.candidates.find((c) => c.frame === focusedEntry?.selected[0]?.frame) ||
    focusedEntry?.candidates[0] ||
    null;

  const onFocus = (entryId, frame) => setFocus({ entryId, frame: frame || null });

  // The first pending row after the focused one, in page order, wrapping round: a position
  // counter would skip rows as decided ones leave the pending list.
  const goToNextPending = () => {
    if (pending.length === 0) return;
    const at = visible.findIndex((e) => e.id === focus.entryId);
    const ahead = visible.slice(at + 1).concat(visible.slice(0, Math.max(at, 0)));
    const target = ahead.find((e) => e.status === 'pending') || pending[0];
    onFocus(target.id, target.selected[0]?.frame || target.candidates[0]?.frame);
    const element = document.getElementById(`entry-${target.id}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const video = videoQuery.data;
  // A photo set is not "a recording" (Anshul, 2026-09-26: "they are not videos/recordings").
  const subjectNoun = video?.kind === 'photos' ? 'this photo set' : 'this recording';

  // Every "write it again" goes through here: with rows on screen, the auditor confirms first.
  const writeNewVersion = () => {
    if (entries.length > 0) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Write a new version of the GRIHA report from ${subjectNoun}? You can keep reviewing these rows while it is written. When it is ready the queue switches to the new version, and these rows, with your edits, stay in the history.`
      );
      if (!ok) return;
    }
    generateMutation.mutate(true);
  };

  const assessment = data?.assessment;
  const attempt = data?.attempt;
  const rewriting = attempt?.state === 'generating';
  // Past the server's limit a new version would 409, so none is offered.
  const generationsUsed = data?.generations_used ?? 0;
  const canWriteAgain = generationsUsed < (data?.generations_max || Infinity);
  const maxPhotos = data?.max_photos_per_row || 4;
  const limitNote = `This run's report has been written ${generationsUsed} times, the most allowed.`;
  const counts = data?.counts || { total: 0, pending: 0, approved: 0, rejected: 0 };
  const decided = counts.approved + counts.rejected;
  const notFound = assessmentQuery.isError && assessmentQuery.error?.response?.status === 404;

  let body;
  if (assessmentQuery.isLoading) {
    body = (
      <div className="animate-pulse space-y-4" data-testid="review-loading">
        <div className="h-16 bg-gray-200 rounded-lg" />
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  } else if (notFound) {
    body = (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Nothing to review yet</h3>
        <p className="mt-1 text-sm text-gray-500">
          {video?.status === 'PROCESSING'
            ? `${video?.kind === 'photos' ? 'This photo set is' : 'This video is'} still being processed. The GRIHA report is written at the end of the run.`
            : `${video?.kind === 'photos' ? 'This photo set has' : 'This video has'} no finished run. Start processing from the Overview tab.`}
        </p>
        <Link
          to={`/projects/${projectId}/videos/${videoId}`}
          className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF]"
        >
          Go to Overview
        </Link>
      </div>
    );
  } else if (assessmentQuery.isError) {
    body = (
      <div className="max-w-lg rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {getApiErrorMessage(assessmentQuery.error, 'Could not load the review queue.')}
      </div>
    );
  } else if (!assessment) {
    body = (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">The GRIHA report for this run has not been written yet</h3>
        <p className="mt-1 text-sm text-gray-500 max-w-xl mx-auto">
          The model reads every shot, the findings and the transcript and writes each row of GRIHA's
          site-visit report for criteria 4, 19 and 23. It takes a few minutes.
        </p>
        <button
          type="button"
          onClick={() => generateMutation.mutate(false)}
          disabled={generateMutation.isLoading}
          className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {generateMutation.isLoading ? 'Starting…' : 'Write the GRIHA report'}
        </button>
      </div>
    );
  } else if (assessment.state === 'generating') {
    body = (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200" data-testid="assessment-generating">
        <ArrowPathIcon className="mx-auto h-10 w-10 text-blue-500 animate-spin" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Writing the GRIHA report…</h3>
        <p className="mt-1 text-sm text-gray-500">
          Reading every shot and the transcript, one criterion at a time. This page updates when it is done.
        </p>
      </div>
    );
  } else {
    body = (
      <>
        {assessment.state === 'failed' && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              <span>
                The report could not be written: {assessment.error || 'no reason was given'}.
                {!canWriteAgain &&
                  ` ${limitNote} ${
                    entries.length > 0
                      ? 'Fill in the rows below by hand.'
                      : 'Re-run the video from the Overview tab to start again.'
                  }`}
              </span>
            </div>
            {canWriteAgain && (
              <button
                type="button"
                onClick={writeNewVersion}
                disabled={generateMutation.isLoading}
                className="flex-shrink-0 px-3 py-1 rounded-md bg-white border border-red-300 text-red-800 hover:bg-red-100 disabled:opacity-50"
              >
                Try again
              </button>
            )}
          </div>
        )}
        {rewriting && (
          <div
            className="mb-4 flex items-start rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
            data-testid="attempt-generating"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2 flex-shrink-0 animate-spin" />
            <span>
              A new version of the report is being written. Keep reviewing this one meanwhile: when the new
              version is ready the queue switches to it, and these rows, with your edits, stay in the history.
            </span>
          </div>
        )}
        {attempt?.state === 'failed' && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              <span>
                The newest version could not be written: {attempt.error || 'no reason was given'}. The rows below
                are from the last version that was.{!canWriteAgain && ` ${limitNote}`}
              </span>
            </div>
            {canWriteAgain && (
              <button
                type="button"
                onClick={writeNewVersion}
                disabled={generateMutation.isLoading}
                className="flex-shrink-0 px-3 py-1 rounded-md bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                Try again
              </button>
            )}
          </div>
        )}
        {assessment.state === 'ready' && assessment.error && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Some rows were not assessed automatically ({assessment.error}). They are marked below.
          </div>
        )}

        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Review Queue</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {decided} of {counts.total} rows decided · {counts.pending} pending
            </p>
          </div>
          <div role="group" aria-label="Filter and act" className="flex items-center flex-wrap gap-2 min-w-0">
            <select
              aria-label="Filter by criterion"
              value={criterionFilter}
              onChange={(e) => setCriterionFilter(e.target.value)}
              className="py-2 pl-3 pr-8 border border-gray-300 rounded-md text-sm bg-white"
            >
              <option value="all">All criteria</option>
              {criteria.map((c) => (
                <option key={c.criterion_id} value={c.criterion_id}>
                  Criterion {c.number}: {c.title}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by decision"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="py-2 pl-3 pr-8 border border-gray-300 rounded-md text-sm bg-white"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {!rewriting && canWriteAgain && (
              <button
                type="button"
                onClick={writeNewVersion}
                disabled={generateMutation.isLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF] disabled:opacity-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-1.5" />
                Write a new version
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
          <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden flex" aria-hidden="true">
            <div className="bg-green-500 h-full" style={{ width: `${counts.total ? (counts.approved / counts.total) * 100 : 0}%` }} />
            <div className="bg-red-500 h-full" style={{ width: `${counts.total ? (counts.rejected / counts.total) * 100 : 0}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between flex-wrap gap-2 text-xs text-gray-600">
            <span>
              {counts.approved} approved · {counts.rejected} rejected (left out of the report) · {counts.pending} pending
            </span>
            {pending.length > 0 && (
              <button
                type="button"
                onClick={goToNextPending}
                className="inline-flex items-center px-2.5 py-1 font-medium rounded-md border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
              >
                <ChevronDoubleDownIcon className="h-4 w-4 mr-1" />
                Next pending row ({pending.length})
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            {/* Capped at the window's height with its own scroll bar (QA 2026-09-29, Divij):
                a sticky panel taller than the window left long camera notes and transcripts
                pinned out of reach, since only the right column scrolled. */}
            <div
              className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto"
              data-testid="focus-panel-scroll"
            >
              <FocusPanel
                focus={{
                  entry: focusedEntry,
                  candidate: focusedCandidate,
                  row: focusedEntry ? catalogueById[draftRows[focusedEntry.id] || focusedEntry.row_id] : null,
                }}
                video={video}
                standardId={data?.standard_id}
              />
            </div>
          </div>
          <div className="lg:col-span-3 space-y-6">
            {visible.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200 text-sm text-gray-500">
                {entries.length === 0 ? 'No rows were written in this version.' : 'No rows match these filters.'}
              </div>
            ) : (
              catalogueGroups.map(({ criterion }) => {
                const inCriterion = visible.filter(
                  (e) => catalogueById[e.row_id]?.criterion_id === criterion.criterion_id
                );
                if (inCriterion.length === 0) return null;
                return (
                  <section key={criterion.criterion_id} aria-label={`Criterion ${criterion.number}`}>
                    <div className="rounded-t-lg bg-[#E2EFDA] border border-[#A9D08E] px-4 py-2">
                      <h3 className="text-sm font-bold text-gray-900">
                        Criterion {criterion.number}: {criterion.title}
                      </h3>
                      {criterion.appraisal_type !== 'Optional' && (
                        <p className="text-xs font-bold uppercase text-red-700">{criterion.appraisal_type}</p>
                      )}
                    </div>
                    <div className="space-y-4 pt-3">
                      {inCriterion.map((entry) => (
                        <EntryBlock
                          key={entry.id}
                          entry={entry}
                          catalogueGroups={catalogueGroups}
                          catalogueById={catalogueById}
                          options={data.compliance_options}
                          isFocused={focus.entryId === entry.id}
                          focusedFrame={focus.entryId === entry.id ? focus.frame : null}
                          onFocus={onFocus}
                          onSave={onSave}
                          onReview={onReview}
                          onDraftRow={onDraftRow}
                          maxPhotos={maxPhotos}
                          videoKind={video?.kind}
                          busy={
                            (saveMutation.isLoading && saveMutation.variables?.entryId === entry.id) ||
                            (reviewMutation.isLoading && reviewMutation.variables?.entryId === entry.id)
                          }
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="p-6">
      <VideoHeaderStrip
        video={video}
        isLoading={videoQuery.isLoading}
        isError={videoQuery.isError}
        error={videoQuery.error}
        projectId={projectId}
        videoId={videoId}
        activeTab="review"
      />
      {body}
    </div>
  );
}
